import React from 'react'
import { IonCol, IonGrid, IonItemDivider, IonLabel, IonList, IonRow } from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getCrewSelector } from '../selectors'
import {
  setBase,
  setPrimaryMonitor,
  setShift,
  setStaffingLevel,
  setVehicle,
  setUnitEquipment,
  setRegistrationTailNumber,
} from '../store/crew.actions'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { IOption, IOptionWithKey } from '../../../common/common.types'
import { IUnitOption } from '../../options/store/options.types'
import { getConfigurationOptionsSelector } from '../../options/selectors'
import { EKG_IMPORT_MODULE_ID } from '../../ekgImport/store/ekgImport.reducers'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { getConfigurationsSelector } from '../../configurations/selectors'
import Validation from '../../../components/validation/Validation.component'
import { TripInformationConstants } from '../../tripInformation/store/tripInformation.constants'

export const Shift = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const configurationOptions = useSelector(getConfigurationOptionsSelector)
  const { serviceModules } = configurationOptions
  const configurationsState: IConfigurationsState = useSelector(getConfigurationsSelector)
  const { serviceConfig } = configurationsState
  const { PR_TAIL_NUMBER } = serviceConfig

  const crew = useSelector(getCrewSelector)
  const {
    shift,
    base,
    staffingLevel,
    vehicle,
    vehicleOptions,
    unitEquipment,
    unitEquipmentOptions,
    primaryMonitor,
    primaryMonitorOptions,
    shiftOptions,
    baseOptions,
    staffingLevelOptions,
    registrationTailNumber,
    registrationTailOptions,
  } = crew

  const shiftOnChange = (option: IOption) => {
    dispatch(setShift(option))
  }

  const baseOnChange = (option: IOption) => {
    dispatch(setBase(option))
  }

  const staffingLevelOnChange = (option: IOption) => {
    dispatch(setStaffingLevel(option))
  }

  const vehicleOnChange = (option: IUnitOption) => {
    dispatch(setVehicle(option))
    if (option && option.equipmentLevel !== null) {
      dispatch(setUnitEquipment(unitEquipmentOptions.find((uEquipment) => uEquipment.id === option.equipmentLevel)))
    }
    if (option === null && unitEquipment !== null) {
      dispatch(setUnitEquipment(null))
    }
    const tail = option?.tail_number
    const selectedTail =
      tail && registrationTailOptions
        ? registrationTailOptions.find((r) => r?.name?.toLowerCase() === tail.toLowerCase()) || null
        : null

    dispatch(setRegistrationTailNumber(selectedTail))
  }

  const setRegistrationTailNumberOnChange = (option: IOption) => {
    dispatch(setRegistrationTailNumber(option))
  }

  const unitEquipmentOnChange = (option: IOptionWithKey) => {
    dispatch(setUnitEquipment(option))
  }

  const primaryMonitorOnChange = (option: IOption) => {
    dispatch(setPrimaryMonitor(option))
  }

  return (
    <IonList>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('shift.ShiftDetails')}
        </IonLabel>
      </IonItemDivider>
      <IonGrid className='ion-no-padding'>
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='Shift'
              selectedOption={shift}
              label={t('shift.details.form.Shift')}
              listHeader={t('shift.details.form.Shift')}
              options={shiftOptions}
              action={shiftOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='Base'
              selectedOption={base}
              label={t('shift.details.form.Base')}
              listHeader={t('shift.details.form.Base')}
              options={baseOptions}
              action={baseOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='StaffingLevel'
              selectedOption={staffingLevel}
              label={t('shift.details.form.StaffingLevel')}
              listHeader={t('shift.details.form.StaffingLevel')}
              options={staffingLevelOptions}
              action={staffingLevelOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='Vehicle'
              selectedOption={vehicle}
              label={t('shift.details.form.Unit')}
              listHeader={t('shift.details.form.Unit')}
              options={vehicleOptions}
              action={vehicleOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        {PR_TAIL_NUMBER === 1 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID='RegistrationTailNumber'
                  options={registrationTailOptions}
                  selectedOption={registrationTailNumber}
                  listHeader={t('shift.details.form.registrationTailNumber')}
                  label={t('shift.details.form.registrationTailNumber')}
                  action={setRegistrationTailNumberOnChange}
                />
                <Validation id={TripInformationConstants.SET_REGISTRATION_TAIL_NUMBER} />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='UnitEquipment'
              selectedOption={unitEquipment}
              label={t('shift.details.form.UnitEquipment')}
              listHeader={t('shift.details.form.UnitEquipment')}
              options={unitEquipmentOptions}
              action={unitEquipmentOnChange}
            />
          </IonCol>
        </IonRow>

        {serviceModules.find((i: any) => i.module_id === EKG_IMPORT_MODULE_ID) && (
          <>
            <hr className='field-divider' />
            <IonRow>
              <IonCol>
                <SingleSelect
                  dataTestID='PrimaryMonitor'
                  selectedOption={primaryMonitor}
                  label={t('shift.details.form.PrimaryMonitor')}
                  listHeader={t('shift.details.form.PrimaryMonitor')}
                  options={primaryMonitorOptions}
                  action={primaryMonitorOnChange}
                />
              </IonCol>
            </IonRow>
          </>
        )}
      </IonGrid>
    </IonList>
  )
}
